import { useCallback, useState } from "react";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getStripe, getStripeEnvironment } from "@/lib/stripe";
import { supabase } from "@/integrations/supabase/client";

interface StripeEmbeddedCheckoutProps {
  priceId: string;
  quantity?: number;
  customerEmail?: string;
  returnUrl?: string;
}

export function StripeEmbeddedCheckout({
  priceId,
  quantity,
  customerEmail,
  returnUrl,
}: StripeEmbeddedCheckoutProps) {
  const [failure, setFailure] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const fetchClientSecret = useCallback(async (): Promise<string> => {
    const finalReturnUrl =
      returnUrl ||
      `${window.location.origin}/checkout/return?session_id={CHECKOUT_SESSION_ID}`;
    const { data, error } = await supabase.functions.invoke("create-checkout", {
      body: {
        priceId,
        quantity,
        customerEmail,
        returnUrl: finalReturnUrl,
        environment: getStripeEnvironment(),
      },
    });
    if (error || !data?.clientSecret) {
      const message = error?.message || "Checkout session could not be created";
      setFailure(message);
      throw new Error(message);
    }
    setFailure(null);
    setReady(true);
    return data.clientSecret;
  }, [priceId, quantity, customerEmail, returnUrl]);

  if (failure) {
    return (
      <div className="p-8 text-center" role="alert">
        <AlertTriangle
          className="w-10 h-10 text-secondary mx-auto mb-4"
          aria-hidden="true"
        />
        <h2 className="text-lg font-semibold mb-2">
          We couldn&apos;t open the payment form
        </h2>
        <p className="text-sm text-foreground/80 mb-1 leading-relaxed">
          Your card has not been charged. Try again &mdash; if it fails a second
          time, email{" "}
          <a
            href="mailto:hello@aiforhrmastermind.com"
            className="font-semibold text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm"
          >
            hello@aiforhrmastermind.com
          </a>{" "}
          and we&apos;ll send you a payment link directly.
        </p>
        <p className="text-xs text-muted-foreground font-mono mb-6 break-words">
          {failure}
        </p>
        <Button
          onClick={() => {
            setFailure(null);
            setReady(false);
            setAttempt((n) => n + 1);
          }}
        >
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div id="checkout" className="min-h-[420px]">
      {/* Shown until the checkout session resolves, so the card is never an empty box. */}
      {!ready && (
        <div
          className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground"
          aria-live="polite"
        >
          <Loader2
            className="w-6 h-6 animate-spin motion-reduce:animate-none"
            aria-hidden="true"
          />
          <p className="text-sm">Loading secure payment form&hellip;</p>
          <p className="text-xs">This takes a couple of seconds.</p>
        </div>
      )}
      <EmbeddedCheckoutProvider
        key={attempt}
        stripe={getStripe()}
        options={{ fetchClientSecret }}
      >
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
}
